import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import type { ProductType } from "@/models/product";

type Props = {
    open: boolean;
    missingCategory: ProductType;
    onAddMore: () => void;
    onConfirm: () => void;
    onClose: () => void;
};

export function AddOtherCategoryDialog({
    open,
    missingCategory,
    onAddMore,
    onConfirm,
    onClose,
}: Props) {
    const { t } = useTranslation();
    const ref = useRef<HTMLDialogElement>(null);
    const confirmRef = useRef<HTMLButtonElement>(null);
    const previouslyFocused = useRef<Element | null>(null);

    useEffect(() => {
        const node = ref.current;
        if (!node) return;
        if (open) {
            previouslyFocused.current = document.activeElement;
            if (!node.open) node.showModal();
            confirmRef.current?.focus();
        } else if (node.open) {
            node.close();
            const prev = previouslyFocused.current;
            if (prev instanceof HTMLElement) prev.focus();
        }
    }, [open]);

    const subtitleKey =
        missingCategory === "SUN_PROTECTION"
            ? "addOtherCategoryDialog.subtitleSunProtectionMissing"
            : "addOtherCategoryDialog.subtitleWindowDecorationMissing";

    return (
        <dialog
            ref={ref}
            data-testid="add-other-category-dialog"
            aria-labelledby="add-other-category-dialog-title"
            onCancel={(event) => {
                event.preventDefault();
                onClose();
            }}
            className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-2xl p-0 shadow-xl backdrop:bg-black/40"
        >
            <div className="w-[min(420px,90vw)] p-6 flex flex-col gap-4">
                <h2
                    id="add-other-category-dialog-title"
                    className="text-xl font-bold text-gray-900"
                >
                    {t("addOtherCategoryDialog.title")}
                </h2>
                <p className="text-sm text-gray-600">{t(subtitleKey)}</p>
                <div className="mt-2 flex flex-col gap-2">
                    <button
                        ref={confirmRef}
                        type="button"
                        onClick={onConfirm}
                        className="rounded-full bg-primary px-6 py-3 text-sm font-semibold text-contrast-text hover:bg-primary-dark cursor-pointer"
                    >
                        {t("addOtherCategoryDialog.confirm")}
                    </button>
                    <button
                        type="button"
                        onClick={onAddMore}
                        className="rounded-full border border-gray-300 px-6 py-3 text-sm font-semibold text-gray-700 hover:bg-gray-50 cursor-pointer"
                    >
                        {t("addOtherCategoryDialog.addMore")}
                    </button>
                </div>
            </div>
        </dialog>
    );
}
